// apps/office/src/components/office/InteriorQuotationPDF.jsx
import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

import iqClient  from '../../pages/public/templates/iq-client.png';
import iqProject from '../../pages/public/templates/iq-project.png';
import iqPayment from '../../pages/public/templates/iq-payment.png';
import iqRate    from '../../pages/public/templates/iq-rate.png';
import f01 from '../../pages/public/templates/iq-faq-01.png';
import f02 from '../../pages/public/templates/iq-faq-02.png';
import f03 from '../../pages/public/templates/iq-faq-03.png';
import f04 from '../../pages/public/templates/iq-faq-04.png';
import f05 from '../../pages/public/templates/iq-faq-05.png';
import f06 from '../../pages/public/templates/iq-faq-06.png';
import f07 from '../../pages/public/templates/iq-faq-07.png';
import f08 from '../../pages/public/templates/iq-faq-08.png';
import f09 from '../../pages/public/templates/iq-faq-09.png';
import f10 from '../../pages/public/templates/iq-faq-10.png';
import f11 from '../../pages/public/templates/iq-faq-11.png';
import f12 from '../../pages/public/templates/iq-faq-12.png';
import f13 from '../../pages/public/templates/iq-faq-13.png';
import f14 from '../../pages/public/templates/iq-faq-14.png';

const FAQ = [f01, f02, f03, f04, f05, f06, f07, f08, f09, f10, f11, f12, f13, f14];
const C = { ink: '#111827', mut: '#6b7280', bd: '#d1d5db', hd: '#f3f4f6' };

const S = StyleSheet.create({
  page: { fontFamily: 'Helvetica', color: C.ink, backgroundColor: '#fff' },
  bg:   { position: 'absolute', top: 0, left: 0, width: 595.28, height: 841.89 },
  abs:  { position: 'absolute' },
  row:  { flexDirection: 'row' },
  bold: { fontWeight: 'bold' },
  r:    { textAlign: 'right' },
  card: { borderWidth: 0.6, borderColor: C.bd, borderRadius: 8, padding: 12, marginBottom: 14, backgroundColor: '#ffffff' },
  tableCard: { borderWidth: 0.8, borderColor: C.bd, borderRadius: 10, backgroundColor: '#ffffff', overflow: 'hidden' },
  tableHead: { flexDirection: 'row', paddingVertical: 11, paddingHorizontal: 12, backgroundColor: '#f8fafc', borderBottomWidth: 0.6, borderBottomColor: C.bd },
  tableRow: { flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 12, backgroundColor: '#ffffff', borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb' },
  tableTotal: { flexDirection: 'row', paddingVertical: 11, paddingHorizontal: 12, backgroundColor: '#ffffff', borderTopWidth: 0.8, borderTopColor: C.bd },
});

const rs   = (v) => 'Rs. ' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v) || 0);
const s    = (v, fb = '-') => (v === null || v === undefined || v === '' ? fb : String(v));
const pArr = (v) => { if (!v) return []; if (Array.isArray(v)) return v; if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch (e) { return []; } } return []; };
const dfmt = (d) => { if (!d) return '-'; try { const t = new Date(d); return String(t.getDate()).padStart(2,'0')+'-'+String(t.getMonth()+1).padStart(2,'0')+'-'+t.getFullYear(); } catch (e) { return '-'; } };

function words(n) {
  n = Math.floor(Number(n) || 0);
  if (!n) return 'Zero rupees only';
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const t2 = (x) => (x < 20 ? a[x] : b[Math.floor(x/10)] + (x%10 ? ' '+a[x%10] : ''));
  const t3 = (x) => (x >= 100 ? a[Math.floor(x/100)]+' Hundred'+(x%100 ? ' '+t2(x%100) : '') : t2(x));
  const p = []; let cr = Math.floor(n/1e7); n %= 1e7; let lk = Math.floor(n/1e5); n %= 1e5; let th = Math.floor(n/1e3); n %= 1e3;
  if (cr) p.push(t3(cr)+' Crore'); if (lk) p.push(t3(lk)+' Lakh'); if (th) p.push(t3(th)+' Thousand'); if (n) p.push(t3(n));
  return p.join(' ').trim() + ' rupees only';
}

const InteriorQuotationPDF = ({ quotation, project, client, imageMap = {} }) => {
  const items = pArr(quotation && quotation.items);
  const profitPct = Number(quotation && quotation.profitMargin) || 20;
  const profitFactor = 1 + profitPct / 100;
  const rows = items.map((b) => {
    const prods = pArr(b.products || b.items);
    const mod = prods.reduce((a, x) => a + ((Number(x.amount) || 0) * profitFactor), 0);
    const gst = mod * 0.18;
    const rl = Number(b.roomLength) || 0, rw = Number(b.roomWidth) || 0;
    return { name: s(b.groupName || b.subcategoryName), size: rl && rw ? rl+' x '+rw : '-', area: Number(b.roomArea) || 0, mod, gst, total: mod+gst };
  });
  const sumArea = rows.reduce((a, r) => a + r.area, 0);
  const sumMod  = rows.reduce((a, r) => a + r.mod, 0);
  const subTotal = sumMod;
  const tq       = subTotal;
  const transport = Number(quotation && quotation.transportation) || 0;
  const cleaning  = Number(quotation && quotation.deepCleaning) || 0;
  const discType  = (quotation && quotation.discountType) || 'percentage';
  const discValue = Number(quotation && quotation.discountValue) || 0;
  const disc      = discType === 'percentage'
    ? subTotal * discValue / 100
    : discValue;
  const initialQuote = subTotal - disc;
  const cgstEnabled = !!(quotation && quotation.cgstEnabled);
  const cgstRate = Number(quotation && quotation.cgstRate) || 9;
  const cgstAmt = cgstEnabled ? initialQuote * cgstRate / 100 : 0;
  const sgstEnabled = !!(quotation && quotation.sgstEnabled);
  const sgstRate = Number(quotation && quotation.sgstRate) || 9;
  const sgstAmt = sgstEnabled ? initialQuote * sgstRate / 100 : 0;
  const grandTotal = initialQuote + cgstAmt + sgstAmt;
  const finalQuoteValue = grandTotal + transport + cleaning;
  const final = finalQuoteValue;

  // Chunk rooms into pages (~600pt content per page)
  const chunks = []; let cur = [], curH = 0;
  for (const blk of items) {
    const prods = pArr(blk.products || blk.items);
    const h = 130 + prods.length * 18;
    if (curH + h > 620 && cur.length > 0) { chunks.push(cur); cur = []; curH = 0; }
    cur.push(blk); curH += h;
  }
  if (cur.length || chunks.length === 0) chunks.push(cur);

  const X = [49, 208, 337, 467];  // Property Name/Buildup Area/Design Style/Design Quality

  return (
    <Document>
      {/* PAGE 1 - CLIENT DETAILS */}
      <Page size="A4" style={S.page}>
        <Image src={iqClient} style={S.bg} fixed />

        {(() => {
          const jid = (client && client.jobId) || (project && project.jobId) || '';
          const qid = (quotation && quotation.quoteNumber) || '';
          if (!jid && !qid) return null;
          return (
            <View style={[S.abs, { top: 110, left: 355, width: 195 }]}>
              {jid ? <Text style={[S.bold, { fontSize: 10, textAlign: 'right' }]}>Job ID : {jid}</Text> : null}
              {qid ? <Text style={[S.bold, { fontSize: 10, textAlign: 'right', marginTop: 3 }]}>Quotation ID : {qid}</Text> : null}
            </View>
          );
        })()}

        <Text style={[S.abs, S.bold, { top: 155, left: 45, fontSize: 20 }]}>
          Hello, {s(client && (client.name || client.clientName), 'Client')}.
        </Text>
        <Text style={[S.abs, { top: 185, left: 45, width: 500, fontSize: 9.5, color: C.mut, lineHeight: 1.4 }]}>
          Your project interior quotation is ready. Take a look and let us know how we can make it perfect for you.
        </Text>

        {/* Row 1 — labels at top=293.8/294.7, values at top=301 (up 15) */}
        <Text style={[S.abs, S.bold, { top: 301, left: 49.7,  width: 155, fontSize: 11 }]}>{s(project && project.propertyName)}</Text>
        <Text style={[S.abs, { top: 301, left: 207, width: 120, fontSize: 11 }]}>
          {client && client.carpetArea ? client.carpetArea + ' sqft.' : '-'}
        </Text>
        <Text style={[S.abs, { top: 301, left: 330, width: 120, fontSize: 11 }]}>{s(quotation && quotation.mainCategory, 'Standard')}</Text>
        <Text style={[S.abs, { top: 301, left: 452, width: 100, fontSize: 11 }]}>{s(items[0] && items[0].subcategoryName, '-')}</Text>

        {/* Row 2 — labels at top=353.5, values at top=361 (up 15) */}
        <Text style={[S.abs, { top: 361, left: 49.7,  width: 155, fontSize: 11 }]}>{s(client && client.propertyType)}</Text>
        <Text style={[S.abs, { top: 361, left: 207,   width: 350, fontSize: 11 }]}>{s(project && project.propertyAddress)}</Text>

        {/* Row 3 — labels at top=415.7/409.3, values at top=423 (up 15) */}
        <Text style={[S.abs, { top: 423, left: 49.7,  width: 155, fontSize: 11 }]}>{dfmt(quotation && quotation.created)}</Text>
        <Text style={[S.abs, { top: 423, left: 209.2, width: 210, fontSize: 11 }]}>{dfmt(project && project.workStartDate)}</Text>
        <Text style={[S.abs, { top: 423, left: 426.5, width: 145, fontSize: 11 }]}>{dfmt(project && project.workEndDate)}</Text>
      </Page>

      {/* PAGE 2 - ROOM SUMMARY + PRICE SUMMARY */}
      <Page size="A4" style={S.page}>
        <Image src={iqProject} style={S.bg} fixed />

        {/* ═══════════ ROOM SUMMARY — column stack ═══════════ */}
        <View style={[S.abs, { top: 98, left: 40, width: 515 }]}>

          {/* Card 1: Table + Sub Total */}
          <View style={{ borderWidth: 0.8, borderColor: '#d1d5db', borderRadius: 10, backgroundColor: '#ffffff', marginBottom: 2 }}>
            <View style={{ flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb' }}>
              <Text style={[S.bold, { flex: 2.2, fontSize: 9, color: '#111827' }]}>Room Name</Text>
              <Text style={[S.bold, { flex: 1.2, fontSize: 9, color: '#111827' }]}>Room Size</Text>
              <Text style={[S.bold, { flex: 1.2, fontSize: 9, color: '#111827' }]}>Room Area</Text>
              <Text style={[S.bold, S.r, { flex: 1.3, fontSize: 9, color: '#111827' }]}>Modules</Text>
            </View>
            {rows.slice(0, 10).map((r, j) => (
              <View key={j} style={{ flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 0.4, borderBottomColor: '#f3f4f6' }}>
                <Text style={{ flex: 2.2, fontSize: 9, color: '#374151' }}>{r.name}</Text>
                <Text style={{ flex: 1.2, fontSize: 9, color: '#374151' }}>{r.size}</Text>
                <Text style={{ flex: 1.2, fontSize: 9, color: '#374151' }}>{r.area ? r.area + ' sqft.' : '-'}</Text>
                <Text style={[S.r, S.bold, { flex: 1.3, fontSize: 9, color: '#111827' }]}>{rs(r.mod)}</Text>
              </View>
            ))}
            <View style={{ flexDirection: 'row', paddingVertical: 11, paddingHorizontal: 14, borderTopWidth: 0.8, borderTopColor: '#9ca3af' }}>
              <Text style={[S.bold, { flex: 2.2, fontSize: 10, color: '#111827' }]}>Sub Total :</Text>
              <Text style={{ flex: 1.2 }}></Text>
              <Text style={[S.bold, { flex: 1.2, fontSize: 10, color: '#111827' }]}>{sumArea} sqft.</Text>
              <Text style={[S.bold, S.r, { flex: 1.3, fontSize: 10, color: '#111827' }]}>{rs(subTotal)}</Text>
            </View>
          </View>

          {/* Card 2: Discount (if any) */}
          {disc > 0 && (
            <View style={{ borderWidth: 0.8, borderColor: '#d1d5db', borderRadius: 10, backgroundColor: '#ffffff', flexDirection: 'row', paddingVertical: 11, paddingHorizontal: 14, marginBottom: 2 }}>
              <Text style={{ flex: 3, fontSize: 10, color: '#374151', paddingLeft: 100 }}>
                Discount {discType === 'percentage' ? '(' + discValue + '%)' : '(' + ((disc / subTotal) * 100).toFixed(1) + '%)'}
              </Text>
              <Text style={[S.r, S.bold, { flex: 2, fontSize: 10, color: '#111827' }]}>- {rs(disc)}</Text>
            </View>
          )}

          {/* Card 3: Initial Quote Value */}
          <View style={{ borderWidth: 0.8, borderColor: '#9ca3af', borderRadius: 10, backgroundColor: '#f9fafb', flexDirection: 'row', paddingVertical: 12, paddingHorizontal: 14 }}>
            <Text style={{ flex: 3, fontSize: 11, fontWeight: 'bold', color: '#111827' }}>Initial Quote Value :</Text>
            <Text style={[S.r, { flex: 2, fontSize: 11, fontWeight: 'bold', color: '#111827' }]}>{rs(initialQuote)}</Text>
          </View>

        </View>

        {/* ═══════════ PRICE SUMMARY — column stack ═══════════ */}
        <View style={[S.abs, { top: 489, left: 40, width: 515 }]}>

          {/* Card 1: Initial + CGST + SGST + GRAND TOTAL */}
          <View style={{ borderWidth: 0.8, borderColor: '#d1d5db', borderRadius: 10, backgroundColor: '#ffffff', marginBottom: 2 }}>
            <View style={{ flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 14 }}>
              <Text style={{ flex: 3, fontSize: 10, color: '#374151' }}>Initial Quote Value</Text>
              <Text style={[S.r, S.bold, { flex: 2, fontSize: 10, color: '#111827' }]}>{rs(initialQuote)}</Text>
            </View>
            {cgstEnabled && cgstAmt > 0 && (
              <View style={{ flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 14, borderTopWidth: 0.4, borderTopColor: '#e5e7eb' }}>
                <Text style={{ flex: 3, fontSize: 10, color: '#374151', paddingLeft: 100 }}>CGST @ {cgstRate}%</Text>
                <Text style={[S.r, S.bold, { flex: 2, fontSize: 10, color: '#111827' }]}>+ {rs(cgstAmt)}</Text>
              </View>
            )}
            {sgstEnabled && sgstAmt > 0 && (
              <View style={{ flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 14, borderTopWidth: 0.4, borderTopColor: '#e5e7eb' }}>
                <Text style={{ flex: 3, fontSize: 10, color: '#374151', paddingLeft: 100 }}>SGST @ {sgstRate}%</Text>
                <Text style={[S.r, S.bold, { flex: 2, fontSize: 10, color: '#111827' }]}>+ {rs(sgstAmt)}</Text>
              </View>
            )}
            <View style={{ flexDirection: 'row', paddingVertical: 12, paddingHorizontal: 14, borderTopWidth: 0.8, borderTopColor: '#9ca3af' }}>
              <Text style={{ flex: 3, fontSize: 11, fontWeight: 'bold', color: '#111827' }}>GRAND TOTAL</Text>
              <Text style={[S.r, { flex: 2, fontSize: 11, fontWeight: 'bold', color: '#111827' }]}>{rs(grandTotal)}</Text>
            </View>
          </View>

          {/* Card 2: Additional Charges (if any) */}
          {(transport > 0 || cleaning > 0) && (
            <View style={{ borderWidth: 0.8, borderColor: '#d1d5db', borderRadius: 10, backgroundColor: '#ffffff', paddingVertical: 10, paddingHorizontal: 14, marginBottom: 2 }}>
              <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#374151', marginBottom: 6 }}>Additional Charges :</Text>
              {transport > 0 && (
                <View style={{ flexDirection: 'row', paddingVertical: 3 }}>
                  <Text style={{ flex: 3, fontSize: 10, color: '#6b7280', paddingLeft: 100 }}>Transportation</Text>
                  <Text style={[S.r, S.bold, { flex: 2, fontSize: 10, color: '#111827' }]}>+ {rs(transport)}</Text>
                </View>
              )}
              {cleaning > 0 && (
                <View style={{ flexDirection: 'row', paddingVertical: 3 }}>
                  <Text style={{ flex: 3, fontSize: 10, color: '#6b7280', paddingLeft: 100 }}>Deep Cleaning</Text>
                  <Text style={[S.r, S.bold, { flex: 2, fontSize: 10, color: '#111827' }]}>+ {rs(cleaning)}</Text>
                </View>
              )}
            </View>
          )}

          {/* Card 3: Final Quote Value (golden) */}
          <View style={{ borderWidth: 1, borderColor: '#f59e0b', borderRadius: 10, backgroundColor: '#fffbeb', flexDirection: 'row', paddingVertical: 13, paddingHorizontal: 14, marginBottom: 2 }}>
            <Text style={{ flex: 3, fontSize: 12, fontWeight: 'bold', color: '#78350f' }}>FINAL QUOTE VALUE</Text>
            <Text style={[S.r, { flex: 2, fontSize: 13, fontWeight: 'bold', color: '#78350f' }]}>{rs(finalQuoteValue)}</Text>
          </View>

          {/* Card 4: Amount in words */}
          <View style={{ borderWidth: 0.6, borderColor: '#d1d5db', borderRadius: 8, backgroundColor: '#ffffff', paddingVertical: 7, paddingHorizontal: 12 }}>
            <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>
              {words(finalQuoteValue).toUpperCase()}
            </Text>
          </View>

        </View>
      </Page>

      {/* PAGE 3 - PAYMENT PROCESS */}
      <Page size="A4" style={S.page}>
        <Image src={iqPayment} style={S.bg} fixed />
        <Text style={[S.abs, S.bold, S.r, { top: 130, left: 395, width: 135, fontSize: 10.5 }]}>{rs(Math.max(20000, final * 0.05))}</Text>
        <Text style={[S.abs, S.bold, S.r, { top: 170, left: 395, width: 135, fontSize: 10.5 }]}>{rs(final * 0.10)}</Text>
        <Text style={[S.abs, S.bold, S.r, { top: 210, left: 395, width: 135, fontSize: 10.5 }]}>{rs(final * 0.20)}</Text>
        <Text style={[S.abs, S.bold, S.r, { top: 250, left: 395, width: 135, fontSize: 10.5 }]}>{rs(final * 0.30)}</Text>
        <Text style={[S.abs, S.bold, S.r, { top: 290, left: 395, width: 135, fontSize: 10.5 }]}>{rs(final * 0.25)}</Text>
        <Text style={[S.abs, S.bold, S.r, { top: 330, left: 395, width: 135, fontSize: 10.5 }]}>{rs(final * 0.10)}</Text>
        <Text style={[S.abs, S.bold, S.r, { top: 368, left: 395, width: 135, fontSize: 11.5 }]}>{rs(final)}</Text>
      </Page>

      {/* PAGE 4+ - RATE ANALYSIS (auto-chunked) */}
      {chunks.map((chunk, ci) => (
        <Page key={'ra'+ci} size="A4" style={[S.page, { paddingTop: 100, paddingBottom: 100, paddingHorizontal: 40 }]}>
          <Image src={iqRate} style={S.bg} fixed />
          <View>
            {chunk.map((blk, i) => {
              const prods = pArr(blk.products || blk.items);
              const roomName = s(blk.groupName, 'Room');
              const sub = s(blk.subcategoryName, '');
              const quality = s(blk.mainCategory || (quotation && quotation.mainCategory), 'Standard');
              const area = Number(blk.roomArea) || 0;
              const total = prods.reduce((a, x) => a + ((Number(x.amount) || 0) * profitFactor), 0);
              return (
                <View key={i} style={S.card}>
                  <Text style={[S.bold, { fontSize: 12, marginBottom: 2 }]}>{roomName}</Text>
                  {sub ? <Text style={{ fontSize: 9.5, color: C.mut, marginBottom: 2 }}>{sub}</Text> : null}
                  <Text style={{ fontSize: 9, color: C.mut, marginBottom: 8 }}>
                    Quality - {quality}{area ? '   |   Area: ' + area + ' sqft.' : ''}
                  </Text>
                  <View style={[S.row, { paddingVertical: 5, paddingHorizontal: 8, backgroundColor: '#ffffff', borderBottomWidth: 0.5, borderBottomColor: C.bd }]}>
                    <Text style={[S.bold, { flex: 3, fontSize: 9 }]}>Product</Text>
                    <Text style={[S.bold, S.r, { flex: 1.5, fontSize: 9 }]}>Amount</Text>
                  </View>
                  {prods.map((p, j) => {
                    const imgUrl = (p.image && p.productId && imageMap[p.productId]) || null;
                    return (
                      <View key={j} style={[S.row, { paddingVertical: 6, paddingHorizontal: 8, borderTopWidth: 0.3, borderTopColor: '#eaeaea', alignItems: 'center' }]}>
                        {imgUrl ? (
                          <Image
                            src={imgUrl}
                            cache={false}
                            style={{ width: 26, height: 26, marginRight: 10, borderRadius: 3, objectFit: 'cover' }}
                          />
                        ) : (
                          <View style={{ width: 26, height: 26, marginRight: 10, borderRadius: 3, backgroundColor: '#f3f4f6', borderWidth: 0.3, borderColor: '#e5e7eb' }} />
                        )}
                        <Text style={{ flex: 3, fontSize: 9 }}>{s(p.name)}</Text>
                        <Text style={[S.r, S.bold, { flex: 1.5, fontSize: 9 }]}>{rs((Number(p.amount) || 0) * profitFactor)}</Text>
                      </View>
                    );
                  })}
                  <View style={[S.row, { marginTop: 6, paddingVertical: 5, paddingHorizontal: 8, borderTopWidth: 0.5, borderTopColor: C.bd }]}>
                    <Text style={[S.bold, { flex: 3, fontSize: 10 }]}>Total :</Text>
                    <Text style={[S.bold, S.r, { flex: 1.5, fontSize: 10 }]}>{rs(total)}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </Page>
      ))}

      {/* FAQ PAGES */}
      {FAQ.map((src, i) => (
        <Page key={'faq'+i} size="A4" style={S.page}>
          <Image src={src} style={S.bg} fixed />
        </Page>
      ))}
    </Document>
  );
};

export default InteriorQuotationPDF;
